import React, { useState } from 'react';
import { X, ChevronRight, ChevronDown, Star, MapPin, Cloud, Cpu, Clock, Search, Crosshair, Globe } from 'lucide-react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ModelCard } from './ModelCard';
import { Card } from '../ui/Card';
import { cn } from '../ui/Button';
interface ForecastSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}
interface SettingsCategoryProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  starCount?: number;
  metaLabel?: string;
  isLast?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  children?: React.ReactNode;
}
// Custom pink marker
const pinkIcon = new L.DivIcon({
  html: '<div style="width:14px;height:14px;background:#EC4899;border-radius:50%;border:2px solid white;box-shadow:0 2px 6px rgba(236,72,153,0.5);"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
  className: ''
});
const locationFavorites = [{
  name: 'London',
  active: false
}, {
  name: 'Enge',
  active: true
}, {
  name: 'oxford',
  active: false
}];
const horizonOptions = [{
  label: '1 Day',
  hours: 24
}, {
  label: '2 Days',
  hours: 48
}, {
  label: '3 Days',
  hours: 72
}, {
  label: '4 Days',
  hours: 96
}, {
  label: '5 Days',
  hours: 120
}, {
  label: '20 Days',
  hours: 480
}, {
  label: '60 Days',
  hours: 1440
}];
const weatherParameters = [{
  name: 'Air Temperature 2m',
  description: 'Temperature measured at 2 meters above ground',
  unit: '°C',
  isStarred: true,
  isActive: true
}, {
  name: 'Dew Point Temperature 2m',
  description: 'Dew Point Temperature measured at 2 meters above ground',
  unit: '°C',
  isStarred: true
}, {
  name: 'Wind Speed 10m',
  description: 'Wind Speed measured at 10 meters above ground',
  unit: 'm s⁻¹',
  isStarred: true
}, {
  name: 'Low Cloud Cover',
  description: 'Low altitude cloud coverage',
  unit: '%',
  isStarred: true
}, {
  name: 'Precipitation',
  description: 'Total precipitation amount',
  unit: 'mm'
}, {
  name: 'Wind Direction 10m',
  description: 'Wind Direction measured at 10 meters above ground',
  unit: '°'
}, {
  name: 'Wind Speed 100m',
  description: 'Wind Speed measured at 100 meters above ground',
  unit: 'm s⁻¹'
}, {
  name: 'Wind Direction 100m',
  description: 'Wind Direction measured at 100 meters above ground',
  unit: '°'
}, {
  name: 'High Cloud Cover',
  description: 'High altitude cloud coverage',
  unit: '%'
}, {
  name: 'Medium Cloud Cover',
  description: 'Medium altitude cloud coverage',
  unit: '%'
}, {
  name: 'Mean Sea-level Pressure',
  description: 'Atmospheric pressure adjusted to sea level',
  unit: 'hPa'
}, {
  name: 'Geopotential',
  description: 'Geopotential at 500 hPa pressure level',
  unit: 'm² s⁻²'
}];
function ParameterCard({
  name,
  description,
  unit,
  isStarred,
  isActive
}: {
  name: string;
  description: string;
  unit: string;
  isStarred?: boolean;
  isActive?: boolean;
}) {
  return <Card className={cn('p-3.5 cursor-pointer transition-all hover:bg-slate-800/50', isActive ? 'border-[#EC4899] ring-1 ring-[#EC4899]/20' : 'border-slate-800')} data-id="element-648">
      <div className="flex items-start justify-between mb-1" data-id="element-649">
        <span className="font-bold text-sm text-slate-100" data-id="element-650">{name}</span>
        <Star size={14} className={cn('flex-shrink-0 mt-0.5', isStarred ? 'text-[#EC4899] fill-[#EC4899]' : 'text-slate-600 hover:text-slate-400')} data-id="element-651" />
      </div>
      <p className="text-xs text-slate-400 leading-relaxed mb-1.5" data-id="element-652">
        {description}
      </p>
      <span className="text-xs font-medium text-[#EC4899]" data-id="element-653">{unit}</span>
    </Card>;
}
function SettingsCategory({
  icon,
  title,
  subtitle,
  starCount,
  metaLabel,
  isLast,
  isExpanded,
  onToggle,
  children
}: SettingsCategoryProps) {
  const Chevron = isExpanded ? ChevronDown : ChevronRight;
  return <div className={!isLast && !isExpanded ? 'border-b border-slate-800/60' : ''} data-id="element-654">
      <button onClick={onToggle} className={`flex items-center gap-3 w-full px-5 py-4 text-left transition-colors ${isExpanded ? 'bg-white/[0.02]' : 'hover:bg-white/[0.03]'}`} data-id="element-655">
        <Chevron size={14} className={`flex-shrink-0 transition-colors ${isExpanded ? 'text-slate-400' : 'text-slate-600'}`} data-id="element-656" />
        <div className="flex-1 min-w-0" data-id="element-657">
          <div className="flex items-center gap-2" data-id="element-658">
            {icon}
            <span className="text-sm font-medium text-slate-200" data-id="element-659">{title}</span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5 pl-6" data-id="element-660">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-661">
          {starCount !== undefined && <div className="flex items-center gap-1" data-id="element-662">
              <Star size={12} className="text-[#EC4899] fill-[#EC4899]" data-id="element-663" />
              <span className="text-xs text-slate-400" data-id="element-664">{starCount}</span>
            </div>}
          {metaLabel && <span className="text-xs text-slate-500" data-id="element-665">{metaLabel}</span>}
        </div>
      </button>
      {isExpanded && children && <div className="px-5 pb-4 pt-2 border-b border-slate-800/60" data-id="element-666">
          {children}
        </div>}
    </div>;
}
function HorizonGrid() {
  const [selected, setSelected] = useState('60 Days');
  return <div className="grid grid-cols-2 gap-2" data-id="element-667">
      {horizonOptions.map(opt => {
      const isActive = opt.label === selected;
      return <button key={opt.label} onClick={() => setSelected(opt.label)} className={`flex flex-col items-start px-4 py-3 rounded-lg border transition-colors text-left ${isActive ? 'border-[#EC4899] ring-1 ring-[#EC4899]/20 bg-[#EC4899]/10' : 'border-slate-700/50 bg-slate-800/40 hover:bg-slate-800/70 hover:border-slate-600'}`} data-id="element-668">
            <span className={`text-sm font-semibold ${isActive ? 'text-white' : 'text-slate-200'}`} data-id="element-669">
              {opt.label}
            </span>
            <span className={`text-xs ${isActive ? 'text-slate-300' : 'text-slate-500'}`} data-id="element-670">
              {opt.hours} hours
            </span>
          </button>;
    })}
    </div>;
}
export function ForecastSettings({
  isOpen,
  onClose
}: ForecastSettingsProps) {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const toggleCategory = (name: string) => {
    setExpandedCategory(prev => prev === name ? null : name);
  };
  return <>
      {isOpen && <div className="absolute inset-0 bg-black/20 z-40 transition-opacity" onClick={onClose} data-id="element-671" />}

      <div className="absolute top-0 right-0 h-full w-[360px] z-50 border-l border-slate-800 bg-[#0F172A] flex flex-col transition-transform duration-300 ease-in-out" style={{
      transform: isOpen ? 'translateX(0)' : 'translateX(100%)'
    }} data-id="element-672">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800" data-id="element-673">
          <h3 className="font-semibold text-slate-200" data-id="element-674">Forecast Settings</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors" data-id="element-675">
            <X size={18} data-id="element-676" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto" data-id="element-677">
          {/* Location */}
          <SettingsCategory icon={<MapPin size={16} className="text-slate-400" data-id="element-679" />} title="Location" subtitle="Enge" starCount={3} metaLabel="1 recent" isExpanded={expandedCategory === 'location'} onToggle={() => toggleCategory('location')} data-id="element-678">
            <div className="relative mb-3" data-id="element-680">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-681" />
              <input type="text" placeholder="Search location..." className="w-full h-9 pl-9 pr-3 rounded-md bg-slate-800/60 border border-slate-700/50 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-[#EC4899]/40 focus:ring-1 focus:ring-[#EC4899]/20" data-id="element-682" />
            </div>

            <div className="rounded-lg overflow-hidden border border-slate-700/50 mb-3 h-[180px]" data-id="element-683">
              <MapContainer center={[47.366, 8.548]} zoom={10} className="h-full w-full" zoomControl={true} attributionControl={false} data-id="element-684">
                <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" data-id="element-685" />
                <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png" opacity={0.7} data-id="element-686" />
                <Marker position={[47.366, 8.548]} icon={pinkIcon} data-id="element-687" />
              </MapContainer>
            </div>

            <div className="space-y-2 mb-4" data-id="element-688">
              <button className="flex items-center justify-center gap-2 w-full py-2 rounded-md border border-slate-700/50 bg-slate-800/30 text-[#EC4899] text-sm font-medium hover:bg-slate-800/60 transition-colors" data-id="element-689">
                <Crosshair size={14} data-id="element-690" />
                Use Current Location
              </button>
              <button className="flex items-center justify-center gap-2 w-full py-2 rounded-md border border-slate-700/50 bg-slate-800/30 text-[#EC4899] text-sm font-medium hover:bg-slate-800/60 transition-colors" data-id="element-691">
                <Globe size={14} data-id="element-692" />
                Select custom coordinates
              </button>
            </div>

            <div data-id="element-693">
              <div className="flex items-center gap-2 mb-2" data-id="element-694">
                <Star size={12} className="text-[#EC4899] fill-[#EC4899]" data-id="element-695" />
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider" data-id="element-696">
                  Favorites
                </span>
              </div>
              <div className="space-y-0.5" data-id="element-697">
                {locationFavorites.map(fav => <button key={fav.name} className="flex items-center justify-between w-full px-2 py-2 rounded-md hover:bg-slate-800/50 transition-colors" data-id="element-698">
                    <span className={`text-sm ${fav.active ? 'text-[#EC4899] font-medium' : 'text-slate-300'}`} data-id="element-699">
                      {fav.name}
                    </span>
                    <Star size={14} className="text-[#EC4899] fill-[#EC4899]" data-id="element-700" />
                  </button>)}
              </div>
            </div>
          </SettingsCategory>

          {/* Weather Parameters */}
          <SettingsCategory icon={<Cloud size={16} className="text-slate-400" data-id="element-702" />} title="Weather Parameters" subtitle="Air Temperature 2m" starCount={4} metaLabel="16 total" isExpanded={expandedCategory === 'parameters'} onToggle={() => toggleCategory('parameters')} data-id="element-701">
            <div className="space-y-3" data-id="element-703">
              {weatherParameters.map(param => <ParameterCard key={param.name} name={param.name} description={param.description} unit={param.unit} isStarred={param.isStarred} isActive={param.isActive} data-id="element-704" />)}
            </div>
          </SettingsCategory>

          {/* Weather Models */}
          <SettingsCategory icon={<Cpu size={16} className="text-slate-400" data-id="element-706" />} title="Weather Models" subtitle="3 selected" starCount={3} metaLabel="24 total" isExpanded={expandedCategory === 'models'} onToggle={() => toggleCategory('models')} data-id="element-705">
            <div className="mb-4" data-id="element-707">
              <div className="flex items-center gap-2 mb-3" data-id="element-708">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider" data-id="element-709">
                  Favorites
                </span>
              </div>
              <div className="space-y-3" data-id="element-710">
                <ModelCard name="EPT-1.5" description="Earth Physics Transformer – global AI weather forecast model from Jua" details="0.081° (~9km) · 6 hours" color="blue" isStarred data-id="element-711" />
                <ModelCard name="EPT-2" description="Earth Physics Transformer – global AI weather forecast model from Jua" details="0.081° (~9km) · 6 hours" color="pink" isStarred isActive data-id="element-712" />
                <ModelCard name="EPT-2e" description="Earth Physics Transformer Ensemble - 30 member global AI ensemble weather..." details="0.25° (~25km) · 6 hours" color="orange" isStarred initTime="Feb 13, 2026, 00:00" data-id="element-713" />
              </div>
            </div>

            <div data-id="element-714">
              <div className="flex items-center gap-2 mb-3" data-id="element-715">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider" data-id="element-716">
                  Jua Models
                </span>
              </div>
              <div className="space-y-3" data-id="element-717">
                <ModelCard name="EPT-1.5 Early" description="Early release of EPT-1.5 with faster updates" details="0.081° (~9km) · 6 hours" color="blue" data-id="element-718" />
                <ModelCard name="EPT-2 Early" description="Early release of EPT-2 with faster updates" details="0.081° (~9km) · 6 hours" color="blue" data-id="element-719" />
                <ModelCard name="EPT-2 HRRR" description="High-resolution rapid refresh forecast model from Jua with 24 updates per day" details="0.05° (~5km) · 1 hour" color="blue" isResearch data-id="element-720" />
                <ModelCard name="EPT-2 Reasoning" description="Global AI weather forecast model with active learning from live data" details="0.081° (~9km) · 6 hours" color="blue" isResearch data-id="element-721" />
              </div>
            </div>
          </SettingsCategory>

          {/* Forecast Horizon */}
          <SettingsCategory icon={<Clock size={16} className="text-slate-400" data-id="element-723" />} title="Forecast Horizon" subtitle="60 Days" metaLabel="1440h" isLast isExpanded={expandedCategory === 'horizon'} onToggle={() => toggleCategory('horizon')} data-id="element-722">
            <HorizonGrid data-id="element-724" />
          </SettingsCategory>
        </div>
      </div>
    </>;
}